import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { useToast } from '@/contexts/ToastContext';
import {
  useKartotekaDefinition,
  useSaveTypeGating,
  useTypeGatedFields,
} from '@/lib/kartoteki/kartoteka-definition';

/**
 * Panel bramkowania pól per typ dokumentu (ADR-0085) — sama treść (lista pól + stopka
 * z licznikiem i przyciskami), bez powłoki modala. Używany przez:
 *  - zakładkę „Pola" edytora typu (`DocumentTypeEditorModal`),
 *  - standalone `TypeFieldGatingModal` (cienki wrapper w `<Modal>`).
 *
 * Admin zaznacza checkboxami, które pola udostępnia dany typ — ODDZIELNIE:
 *  - POLA COROWE (rdzeniowe): domyślnie wszystkie zaznaczone, ale można je ODZNACZYĆ
 *    (ADR-0085). Odznaczenie zapisuje się przez WYKLUCZENIE (`excludedCoreFieldSlugs`);
 *  - POLA SYSTEMOWE (pula klasy): bramkowane przez INKLUZJĘ (`gatedFieldIds`).
 * Zapis = pełne zastąpienie obu zbiorów.
 */
export function TypeFieldGatingPanel({
  slug,
  typeId,
  onClose,
}: {
  slug: string;
  typeId: string;
  /** Zamknięcie kontenera (modal/edytor) — wołane po udanym zapisie i przez „Anuluj". */
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();

  const definition = useKartotekaDefinition(slug);
  const gated = useTypeGatedFields(slug, typeId);
  const save = useSaveTypeGating(slug, typeId);

  // Pola rdzeniowe — domyślnie włączone, ale odznaczalne (ADR-0085): odznaczenie zapisuje
  // się jako WYKLUCZENIE (`excludedCore`), brak wykluczeń = wszystkie rdzeniowe dostępne.
  const coreFields = useMemo(
    () => (definition.data?.fields ?? []).filter((f) => f.isCore),
    [definition.data],
  );
  // Pula klasy = pola dodatkowe — jedyne bramkowane (zaznaczasz, które typ udostępnia).
  const poolFields = useMemo(
    () => (definition.data?.fields ?? []).filter((f) => !f.isCore),
    [definition.data],
  );

  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Slugi pól RDZENIOWYCH wyłączonych dla typu (domyślnie puste = wszystkie core włączone).
  const [excludedCore, setExcludedCore] = useState<Set<string>>(new Set());

  // Zasil stan z serwera przy otwarciu / zmianie typu / dojściu danych bramkowania.
  useEffect(() => {
    if (gated.data) {
      setSelected(new Set(gated.data.gatedFieldIds));
      setExcludedCore(new Set(gated.data.excludedCoreFieldSlugs));
    }
  }, [gated.data, typeId]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Pole rdzeniowe: zaznaczone = NIE wykluczone. Toggle dopisuje/usuwa ze zbioru wykluczeń.
  const toggleCore = (slug: string) => {
    setExcludedCore((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };
  const coreOnCount = coreFields.filter((f) => !excludedCore.has(f.slug)).length;

  const allSelected = poolFields.length > 0 && poolFields.every((f) => selected.has(f.id));
  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(poolFields.map((f) => f.id)));
  };

  const handleSave = () => {
    save.mutate(
      { fieldIds: [...selected], excludedCoreFieldSlugs: [...excludedCore] },
      {
        onSuccess: () => {
          toast.show({ variant: 'success', title: t('adminKartoteki.gatingModal.saved') });
          onClose();
        },
        onError: () => {
          toast.show({ variant: 'error', title: t('adminKartoteki.gatingModal.saveError') });
        },
      },
    );
  };

  const loading = definition.isLoading || gated.isLoading;

  return (
    <div className="flex flex-col">
      {loading ? (
        <p className="py-6 text-center text-sm text-text-secondary">
          {t('adminKartoteki.gatingModal.loading')}
        </p>
      ) : coreFields.length === 0 && poolFields.length === 0 ? (
        <p className="py-6 text-center text-sm text-text-secondary">
          {t('adminKartoteki.gatingModal.empty')}
        </p>
      ) : (
        <div className="space-y-4">
          {coreFields.length > 0 && (
            <div>
              <p className="mb-1.5 text-xs font-medium text-text-secondary">
                {t('adminKartoteki.gatingModal.coreSection')}
              </p>
              <ul className="divide-y divide-border-secondary overflow-hidden rounded-md border border-border-secondary">
                {coreFields.map((f) => (
                  <li key={f.id} className="flex items-center justify-between gap-3 px-3 py-2">
                    <Checkbox
                      checked={!excludedCore.has(f.slug)}
                      onChange={() => toggleCore(f.slug)}
                      containerClassName="min-w-0 flex-1"
                      label={
                        <span className="flex min-w-0 items-center gap-2">
                          <span className="truncate text-text-primary">{f.name}</span>
                          <code className="truncate text-xs text-text-secondary">{f.slug}</code>
                        </span>
                      }
                    />
                    <span className="flex shrink-0 items-center gap-2">
                      <Badge color="gray">
                        {t('moduleTemplateEditor.referencePanel.coreBadge')}
                      </Badge>
                      <Badge color="gray">{f.fieldType}</Badge>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div>
            <div className="mb-1.5 flex items-center justify-between gap-2">
              <p className="text-xs font-medium text-text-secondary">
                {t('adminKartoteki.gatingModal.additionalSection')}
              </p>
              {poolFields.length > 0 && (
                <Button variant="secondary" size="sm" onClick={toggleAll}>
                  {allSelected
                    ? t('adminKartoteki.gatingModal.clearAll')
                    : t('adminKartoteki.gatingModal.selectAll')}
                </Button>
              )}
            </div>
            {poolFields.length === 0 ? (
              <p className="text-sm text-text-secondary">
                {t('adminKartoteki.gatingModal.noAdditional')}
              </p>
            ) : (
              <ul className="divide-y divide-border-secondary rounded-md border border-border-secondary">
                {poolFields.map((f) => (
                  <li key={f.id} className="flex items-center justify-between gap-3 px-3 py-2">
                    <Checkbox
                      checked={selected.has(f.id)}
                      onChange={() => toggle(f.id)}
                      containerClassName="min-w-0 flex-1"
                      label={
                        <span className="flex min-w-0 items-center gap-2">
                          <span className="truncate text-text-primary">{f.name}</span>
                          <code className="truncate text-xs text-text-secondary">{f.slug}</code>
                        </span>
                      }
                    />
                    <span className="flex shrink-0 items-center gap-2">
                      <Badge color="green">
                        {t('moduleTemplateEditor.referencePanel.addedFieldBadge')}
                      </Badge>
                      <Badge color="gray">{f.fieldType}</Badge>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      <div className="mt-4 flex items-center justify-between gap-3 border-t border-border-secondary pt-3">
        <span className="text-sm text-text-secondary">
          {t('adminKartoteki.gatingModal.selectedCountSplit', {
            total: coreOnCount + selected.size,
            core: coreOnCount,
            system: selected.size,
          })}
        </span>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('adminKartoteki.gatingModal.cancel')}
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={save.isPending || loading}>
            {t('adminKartoteki.gatingModal.save')}
          </Button>
        </div>
      </div>
    </div>
  );
}
